export class PcmRecorder {
  private context?: AudioContext;
  private stream?: MediaStream;
  private source?: MediaStreamAudioSourceNode;
  private processor?: ScriptProcessorNode;
  private sink?: GainNode;
  private samples: number[] = [];

  async prepare(): Promise<void> {
    if (this.stream?.active) return;
    this.stream = await navigator.mediaDevices.getUserMedia({
      audio: { channelCount: 1, echoCancellation: true, noiseSuppression: true },
    });
  }

  async start(onFrame: (frame: Uint8Array) => void): Promise<void> {
    await this.prepare();
    this.context = new AudioContext();
    await this.context.resume();
    this.source = this.context.createMediaStreamSource(this.stream!);
    this.processor = this.context.createScriptProcessor(4096, 1, 1);
    this.sink = this.context.createGain();
    this.sink.gain.value = 0;
    this.processor.onaudioprocess = (event) => {
      const input = event.inputBuffer.getChannelData(0);
      const resampled = resample(input, this.context!.sampleRate, 16000);
      for (const sample of resampled) this.samples.push(Math.max(-1, Math.min(1, sample)));
      while (this.samples.length >= 320) {
        const frameSamples = this.samples.splice(0, 320);
        const frame = new Uint8Array(640);
        const view = new DataView(frame.buffer);
        frameSamples.forEach((sample, index) => view.setInt16(index * 2, sample < 0 ? sample * 32768 : sample * 32767, true));
        onFrame(frame);
      }
    };
    this.source.connect(this.processor);
    this.processor.connect(this.sink);
    this.sink.connect(this.context.destination);
  }

  async stop(): Promise<void> {
    this.processor?.disconnect();
    this.source?.disconnect();
    this.sink?.disconnect();
    this.stream?.getTracks().forEach((track) => track.stop());
    await this.context?.close();
    this.context = undefined;
    this.stream = undefined;
    this.samples = [];
  }
}

function resample(input: Float32Array, inputRate: number, outputRate: number): Float32Array {
  if (inputRate === outputRate) return input.slice();
  const ratio = inputRate / outputRate;
  const output = new Float32Array(Math.floor(input.length / ratio));
  for (let i = 0; i < output.length; i += 1) {
    const position = i * ratio;
    const left = Math.floor(position);
    const right = Math.min(input.length - 1, left + 1);
    const mix = position - left;
    output[i] = input[left]! * (1 - mix) + input[right]! * mix;
  }
  return output;
}

export class PcmPlayer {
  private context?: AudioContext;
  private nextTime = 0;
  private sources = new Set<AudioBufferSourceNode>();
  private trailingByte?: number;

  async enqueue(bytes: Uint8Array, sampleRate = 24000): Promise<void> {
    if (!this.context) this.context = new AudioContext();
    await this.context.resume();
    let data = bytes;
    if (this.trailingByte !== undefined) {
      const joined = new Uint8Array(bytes.length + 1);
      joined[0] = this.trailingByte;
      joined.set(bytes, 1);
      data = joined;
      this.trailingByte = undefined;
    }
    if (data.length % 2 === 1) {
      this.trailingByte = data[data.length - 1];
      data = data.slice(0, -1);
    }
    if (!data.length) return;
    const samples = data.length / 2;
    const audioBuffer = this.context.createBuffer(1, samples, sampleRate);
    const channel = audioBuffer.getChannelData(0);
    const view = new DataView(data.buffer, data.byteOffset, data.byteLength);
    for (let index = 0; index < samples; index += 1) channel[index] = view.getInt16(index * 2, true) / 32768;
    const source = this.context.createBufferSource();
    source.buffer = audioBuffer;
    source.connect(this.context.destination);
    const startAt = Math.max(this.context.currentTime + 0.02, this.nextTime);
    source.start(startAt);
    this.nextTime = startAt + audioBuffer.duration;
    this.sources.add(source);
    source.onended = () => this.sources.delete(source);
  }

  stop(): void {
    for (const source of this.sources) {
      try { source.stop(); } catch { /* already stopped */ }
    }
    this.sources.clear();
    this.nextTime = this.context?.currentTime ?? 0;
    this.trailingByte = undefined;
  }

  async close(): Promise<void> {
    this.stop();
    await this.context?.close();
    this.context = undefined;
  }
}
